import { useState, useEffect } from 'react';
import { Layers, Mail, MessageCircle, MessageSquare, ChevronUp, ChevronDown } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { usePermissions } from '@/hooks/use-permissions';
import { useAiSettings, useUpdateAiSettings } from '@/hooks';
import { buildDefaultAiSettings } from '@/lib/ai-settings-defaults';
import type { AiSettings, ChannelOverflow } from '@/types';

// ============================================================================
// Types / constants
// ============================================================================

type OverflowChannel = 'email' | 'livechat' | 'sms';

interface OverflowState {
	email: ChannelOverflow;
	livechat: ChannelOverflow;
	sms: ChannelOverflow;
}

function buildDefault(): OverflowState {
	return {
		email: { enabled: false, thresholdMinutes: 10 },
		livechat: { enabled: false, thresholdMinutes: 5 },
		sms: { enabled: false, thresholdMinutes: 10 },
	};
}

function stateFromSettings(existing: AiSettings | null | undefined): OverflowState {
	const defaults = buildDefault();
	const src = existing?.overflow ?? {};
	return {
		email: src.email ?? defaults.email,
		livechat: src.livechat ?? defaults.livechat,
		sms: src.sms ?? defaults.sms,
	};
}

function overflowEqual(a: OverflowState, b: OverflowState): boolean {
	return JSON.stringify(a) === JSON.stringify(b);
}

// ============================================================================
// StepperInput — number field with up/down chevrons matching Figma design
// ============================================================================

interface StepperInputProps {
	value: number;
	onChange: (v: number) => void;
	disabled: boolean;
}

function StepperInput({ value, onChange, disabled }: StepperInputProps) {
	const increment = () => onChange(value + 1);
	const decrement = () => { if (value > 1) onChange(value - 1); };

	return (
		<div className='flex items-center justify-between h-[40px] bg-white border border-gray-500 rounded-[12px] overflow-hidden pl-3 pr-2 py-2 w-full'>
			<input
				type='number'
				min={1}
				step={1}
				value={value}
				onChange={(e) => {
					const n = parseInt(e.target.value, 10);
					if (!Number.isNaN(n) && n >= 1) onChange(n);
				}}
				disabled={disabled}
				className='flex-1 min-w-0 text-[12px] text-ink bg-transparent outline-none border-none appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
				placeholder='Set Overflow time'
			/>
			<div className='flex flex-col items-center justify-center shrink-0 w-[14px] h-full gap-0'>
				<button
					type='button'
					onClick={increment}
					disabled={disabled}
					aria-label='Increase'
					className='flex items-center justify-center w-[14px] h-[13px] text-gray-800 hover:text-ink cursor-pointer disabled:cursor-not-allowed transition-colors'
				>
					<ChevronUp className='w-[10px] h-[10px]' />
				</button>
				<button
					type='button'
					onClick={decrement}
					disabled={disabled || value <= 1}
					aria-label='Decrease'
					className='flex items-center justify-center w-[14px] h-[13px] text-gray-800 hover:text-ink cursor-pointer disabled:cursor-not-allowed transition-colors'
				>
					<ChevronDown className='w-[10px] h-[10px]' />
				</button>
			</div>
		</div>
	);
}

// ============================================================================
// ChannelCard
// ============================================================================

interface ChannelCardProps {
	icon: React.ReactNode;
	label: string;
	value: ChannelOverflow;
	onChange: (v: ChannelOverflow) => void;
	disabled: boolean;
}

function ChannelCard({ icon, label, value, onChange, disabled }: ChannelCardProps) {
	return (
		<div className='bg-white border border-gray-500 rounded-[16px] overflow-hidden px-4 py-4 flex flex-col gap-4'>
			{/* Header row */}
			<div className='flex items-center justify-between'>
				<div className='flex items-center gap-3'>
					<span className='w-4 h-4 flex items-center justify-center text-ink shrink-0'>
						{icon}
					</span>
					<span className='text-[14px] font-medium text-ink leading-5'>{label}</span>
				</div>
				<Switch
					checked={value.enabled}
					onCheckedChange={(checked) => onChange({ ...value, enabled: checked })}
					disabled={disabled}
				/>
			</div>

			{/* Divider + body — only when enabled */}
			{value.enabled && (
				<>
					{/* Horizontal full-width subtle divider */}
					<div className='w-full h-px bg-gray-300' />

					{/* Threshold field */}
					<div className='flex items-end gap-5'>
						<div className='flex flex-col gap-2 flex-1 min-w-0'>
							<label className='text-[12px] font-medium text-gray-900 leading-4'>
								How long before AI can pick up an unassigned ticket?
							</label>
							<StepperInput
								value={value.thresholdMinutes}
								onChange={(n) => onChange({ ...value, thresholdMinutes: n })}
								disabled={disabled}
							/>
						</div>
						<div className='flex items-center justify-center py-3 shrink-0'>
							<span className='text-[12px] text-gray-800 leading-4'>Minutes</span>
						</div>
					</div>
				</>
			)}
		</div>
	);
}

// ============================================================================
// Page
// ============================================================================

export default function OverflowPage() {
	const { data: settings, isLoading, isError } = useAiSettings();
	const updateMutation = useUpdateAiSettings();
	const { can } = usePermissions();
	const canEdit = can('edit', 'brand');

	const [overflow, setOverflow] = useState<OverflowState>(buildDefault());
	const [savedOverflow, setSavedOverflow] = useState<OverflowState>(buildDefault());

	useEffect(() => {
		const initial = stateFromSettings(settings);
		setOverflow(initial);
		setSavedOverflow(initial);
	}, [settings]);

	const isDirty = !overflowEqual(overflow, savedOverflow);

	const handleChannelChange = (key: OverflowChannel, value: ChannelOverflow) => {
		setOverflow((prev) => ({ ...prev, [key]: value }));
	};

	const handleCancel = () => {
		setOverflow(savedOverflow);
	};

	const handleSave = async () => {
		const base = buildDefaultAiSettings(settings);
		const payload: AiSettings = { ...base, overflow };
		try {
			await updateMutation.mutateAsync(payload);
			setSavedOverflow(overflow);
			toast.success('Overflow rules saved');
		} catch {
			toast.error('Failed to save overflow rules. Please try again.');
		}
	};

	return (
		<div className='flex flex-col w-full min-h-full'>
			{/* Page header bar */}
			<div className='sticky top-0 backdrop-blur-[6px] bg-white/80 border-b border-gray-500 flex items-center h-[60px] px-4 shrink-0 z-10'>
				<p className='text-base font-semibold text-ink leading-6'>Ticket Overflow Rules</p>
			</div>

			{/* Content area */}
			<div className='flex flex-1 flex-col gap-6 p-4 bg-white'>

				{/* ── Intro card ── */}
				<div className='w-[600px] bg-white border border-gray-500 rounded-[16px] overflow-hidden px-4 py-4 flex flex-col gap-4'>
					{/* Dark gradient icon square */}
					<div
						className='w-12 h-12 rounded-[8px] flex items-center justify-center relative shrink-0'
						style={{
							background: 'linear-gradient(156.98deg, var(--color-surface-action) 26.88%, var(--color-surface-action) 102.38%)',
							boxShadow: 'inset 0px 0px 1px 0px rgba(0,0,0,0.18)',
						}}
					>
						<Layers className='w-6 h-6 text-white' />
					</div>

					{/* Title + description */}
					<div className='flex flex-col gap-1'>
						<p className='text-[14px] font-medium text-ink leading-5'>Ticket Overflow Rules</p>
						<p className='text-[12px] text-gray-800 leading-4'>
							{"Overflow only applies to unassigned tickets. If a human has already claimed it, Kim won't touch it. Thresholds apply within your active channels and disposition allowlist."}
						</p>
					</div>
				</div>

				{/* ── Channel cards ── */}
				{isError ? (
					<div className='w-[600px] rounded-[16px] border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-600'>
						Failed to load settings. Please try again later.
					</div>
				) : isLoading ? (
					<div className='w-[600px] flex flex-col gap-3'>
						{(['email', 'livechat', 'sms'] as const).map((key) => (
							<div key={key} className='bg-white border border-gray-500 rounded-[16px] px-4 py-4 space-y-3'>
								<Skeleton className='h-4 w-32' />
								<Skeleton className='h-10 w-full' />
							</div>
						))}
					</div>
				) : (
					<div className='w-[600px] flex flex-col gap-3'>
						<ChannelCard
							icon={<Mail className='w-4 h-4' />}
							label='Email'
							value={overflow.email}
							onChange={(v) => handleChannelChange('email', v)}
							disabled={updateMutation.isPending}
						/>
						<ChannelCard
							icon={<MessageCircle className='w-4 h-4' />}
							label='Live Chat'
							value={overflow.livechat}
							onChange={(v) => handleChannelChange('livechat', v)}
							disabled={updateMutation.isPending}
						/>
						<ChannelCard
							icon={<MessageSquare className='w-4 h-4' />}
							label='SMS'
							value={overflow.sms}
							onChange={(v) => handleChannelChange('sms', v)}
							disabled={updateMutation.isPending}
						/>

					</div>
				)}
			</div>

			{/* Sticky footer — only visible when there are unsaved changes */}
			{isDirty && <div className='sticky bottom-0 border-t border-gray-300 bg-white px-4 py-3 flex items-center justify-end gap-2 shrink-0 z-10'>
				<button
					type='button'
					onClick={handleCancel}
					disabled={!isDirty || updateMutation.isPending}
					className={[
						'h-[30px] px-3 rounded-[20px] border border-gray-300 bg-white text-[12px] font-medium text-ink transition-colors',
						!isDirty || updateMutation.isPending
							? 'cursor-not-allowed opacity-50'
							: 'cursor-pointer hover:bg-gray-100',
					].join(' ')}
				>
					Cancel
				</button>
				<button
					type='button'
					onClick={handleSave}
					disabled={!isDirty || !canEdit || updateMutation.isPending}
					className={[
						'h-[30px] px-3.5 rounded-[100px] bg-ink border border-text-quote text-[12px] font-normal text-gray-50',
						!isDirty || !canEdit || updateMutation.isPending
							? 'cursor-not-allowed opacity-50'
							: 'cursor-pointer hover:bg-ink-strong',
					].join(' ')}
				>
					{updateMutation.isPending ? (
						<span className='flex items-center gap-2'>
							<span className='h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin' />
							Saving…
						</span>
					) : (
						'Save Changes'
					)}
				</button>
			</div>}
		</div>
	);
}
